import type { ArticlePreview } from 'interfaces/content/articles/Articles';
import Link from 'components/Globals/Base/Link';
import useBreakpoint from 'hooks/useBreakpoint';
import type { EventData } from 'services/Gtm/entities';
import { useMemo } from 'react';
import ArticleCard from 'components/Globals/ArticleCard';
import { SectionVariant } from 'interfaces/Section';
import { ARTICLE_CARD_TEST_IDS, ARTICLE_SECTION_TEST_IDS } from 'constants/testsIds/articleComponents';
import { withErrorBoundary } from 'components/ErrorBoundary';
import { UIFragments } from 'utils/log/constants/uiFragments';
import type { ArticleCardLayoutVariant } from 'components/Globals/ArticleCard/articleCardHelpers';
import * as S from './ArticleSectionStyles';

interface PinnedLinks {
  title: string;
  url: string;
}

export interface ArticleSectionProps {
  title?: string;
  titleLink?: string;
  articles: ArticlePreview[];
  pinnedLinks?: PinnedLinks[];
  isReversed?: boolean;
  variant?: SectionVariant;
  className?: string;
  trackingData: EventData;
  dataCy?: string;
  children?: React.ReactNode;
}

const getCardListVariant = (variant: SectionVariant): ArticleCardLayoutVariant => {
  switch (variant) {
    case SectionVariant.TopPicks:
      return 'card-sm';
    case SectionVariant.HomeLatestArticles:
      return 'card-sm';
    case SectionVariant.Section:
      return 'card-md';
    case SectionVariant.SubSection:
      return 'card-lg';
    case SectionVariant.SectionLatestArticles:
      return 'card-sm';
    case SectionVariant.TopArticles2:
      return 'card-xs';
    default:
      return 'card-sm';
  }
};

const ArticleSection = (props: ArticleSectionProps) => {
  const {
    title,
    titleLink,
    articles,
    pinnedLinks,
    trackingData,
    variant = SectionVariant.Section,
    isReversed = false,
    className = '',
    dataCy = '',
    children,
  } = props;

  const isMobile = useBreakpoint('mdMax');
  const isCollapsible = variant === SectionVariant.TopPicks && isMobile;
  const isSectionLatest = variant === SectionVariant.SectionLatestArticles;

  const { heroArticle, articlesList, articlesListSecondary } = useMemo(() => {
    let hero;
    let list;
    let listSecondary;
    switch (variant) {
      case SectionVariant.Section:
        [hero, ...list] = articles;
        break;
      case SectionVariant.SectionLatestArticles:
        [hero] = articles;
        list = articles?.slice(1, 8);
        listSecondary = articles?.slice(8);
        break;
      default:
        list = articles;
        break;
    }
    return { articlesList: list, articlesListSecondary: listSecondary, heroArticle: hero };
  }, [articles, variant]);

  return (
    <S.Wrapper
      $variant={variant}
      $isReversed={isReversed}
      as={isCollapsible ? 'details' : undefined}
      data-cy={dataCy}
      className={className}
    >
      <S.Title
        as={isCollapsible ? 'summary' : 'h2'}
        $fontFamily='saol'
        $size={{
          default: 'level3',
          xlMin:
            [
              SectionVariant.Section,
              SectionVariant.SubSection,
              SectionVariant.SectionLatestArticles,
              SectionVariant.TopArticles2,
            ].indexOf(variant) > -1
              ? 'level2'
              : 'level3',
        }}
        className='section-title'
        data-cy={`${dataCy}-${ARTICLE_SECTION_TEST_IDS.TITLE}`}
      >
        {isCollapsible && <h2>{title}</h2>}
        {!isCollapsible &&
          (!titleLink ? (
            title
          ) : (
            <Link
              href={titleLink}
              type='primary'
              ariaLabel={`Go to ${title}`}
              trackingData={
                trackingData && {
                  ...trackingData,
                  eventAction: 'section title click',
                  eventCategory: 'navigation',
                  eventLabel: `section click : ${trackingData.eventLabel || title}`,
                }
              }
            >
              {title}
            </Link>
          ))}
      </S.Title>

      {pinnedLinks && (
        <S.PinnedLinks
          className='pinned-links'
          data-cy={`${dataCy}-${ARTICLE_SECTION_TEST_IDS.PINNED_LINKS}`}
        >
          {pinnedLinks.map((link) => (
            <Link
              key={link.url}
              href={link.url}
              type='brandAccentSecondary'
              ariaLabel={`Go to ${link.title}`}
              trackingData={
                trackingData && {
                  ...trackingData,
                  eventAction: 'title click',
                  eventCategory: 'navigation',
                  eventLabel: `category click : ${trackingData.eventLabel || link.title}`,
                }
              }
            >
              {link.title}
            </Link>
          ))}
        </S.PinnedLinks>
      )}

      {heroArticle && (
        <ArticleCard
          article={heroArticle}
          layoutVariant='card-xl'
          dataVariant={`hero-${variant}`}
          className='hero-article-card'
          trackingData={trackingData}
          titleSize={isSectionLatest ? { default: 'level6', smMin: 'level3' } : undefined}
          dataCy={ARTICLE_CARD_TEST_IDS.HERO_WRAPPER}
        />
      )}

      {articlesList && articlesList.length > 0 && (
        <S.ArticlesList
          className='articles-list'
          data-cy={`${dataCy}-${ARTICLE_SECTION_TEST_IDS.ARTICLES_LIST}`}
        >
          {articlesList.map((item, index) => {
            const changesDirection = (SectionVariant.Section, SectionVariant.SectionLatestArticles).includes(variant);
            const isLatestArticles = variant === SectionVariant.HomeLatestArticles;
            return (
              <ArticleCard
                key={item.titleLink}
                article={item}
                className={
                  [SectionVariant.HomeLatestArticles, SectionVariant.SectionLatestArticles].includes(variant)
                    ? 'compact'
                    : ''
                }
                layoutVariant={getCardListVariant(variant)}
                dataVariant={`list-${variant}`}
                changesDirection={changesDirection}
                trackingData={trackingData}
                index={isLatestArticles ? index + 1 : undefined}
              />
            );
          })}
        </S.ArticlesList>
      )}
      {articlesListSecondary && articlesListSecondary.length > 0 && (
        <S.ArticlesList
          className='articles-list2'
          data-cy={`${dataCy}-${ARTICLE_SECTION_TEST_IDS.ARTICLES_LIST}2`}
        >
          {articlesListSecondary.map((item) => (
            <ArticleCard
              key={item.titleLink}
              article={item}
              layoutVariant='card-lg'
              dataVariant={`list-secondary-${variant}`}
              titleFontFamily='saol'
              trackingData={trackingData}
              titleSize={isSectionLatest ? { default: 'level6', lgMin: 'level4' } : undefined}
            />
          ))}
        </S.ArticlesList>
      )}
      {children && <S.Children className='children'>{children}</S.Children>}
    </S.Wrapper>
  );
};

export default withErrorBoundary(ArticleSection, { fragment: UIFragments.ARTICLES_SECTION });
